import type { ResponseType, DraftResponse, Ticket, CorrespondenceEntry } from "../types";
import { PIRT_KNOWLEDGE_BASE } from "./pirt-knowledge-base";

/**
 * PIRT AI Drafting Module
 *
 * 8 response types matching actual PIRT workflow:
 * 1. First Contact — Initial triage response to a new ticket
 * 2. Response — Reply to someone's comment/update on the ticket
 * 3. Follow-Up (Nudge) — No response received, checking in
 * 4. Assessment Update — New info received, posting updated analysis
 * 5. Closure — Closing the ticket with summary
 * 6. Legal Escalation — Referral to Legal
 * 7. Redirect — Not PIRT scope, directing elsewhere
 * 8. Policy Answer — Answering a question (not an incident)
 */

// ─── System Prompt (sent to Bedrock as the system message) ───
export const PIRT_SYSTEM_PROMPT = PIRT_KNOWLEDGE_BASE;

// ─── Action-specific prompts ───

const ACTION_PROMPTS: Record<ResponseType, string> = {
  FIRST_CONTACT:
    "Generate a first contact response for this new privacy incident ticket. Acknowledge receipt, provide initial assessment (data classification, severity, scope estimate), note any positive actions already taken by reporter, and ask specific clarifying questions needed to determine next steps. Provide guidance on immediate actions if reporter hasn't taken them.",

  RESPONSE:
    "Generate a response to the latest comment on this ticket. Acknowledge what they said, assess the new information (does it change scope, severity, next steps?), ask follow-up questions if gaps remain, provide guidance or direction based on what they shared, and update PIRT's assessment if the picture has changed. This is a mid-conversation reply — be contextual and specific to what they just told us.",

  FOLLOW_UP_NUDGE:
    "Generate a follow-up nudge message. The person has not responded within the expected timeframe. Reference the original questions/request and when they were posted, restate what's needed (don't make them scroll back), add urgency if regulatory deadlines apply, and optionally set a deadline with consequences of non-response.",

  CLOSURE:
    "Generate a closure comment. All closure criteria must be met: containment confirmed, scope understood, no regulatory obligations outstanding. Summarize what happened (one line), state data involved and classification, confirm containment (how and when), state notification assessment (required or not with reason), provide recommendation for prevention if applicable, state 'No further PIRT action required.'",

  LEGAL_ESCALATION:
    "Generate a legal referral message. Identify correct Legal POC based on jurisdiction (EU → cjohl@amazon.de, US/RoW → jinouths@, Security/3P → stient@). Provide structured referral with: summary, data elements, classification, scope, exposure window, containment status, jurisdictions. Ask specific questions (Art. 33/34? Which DPAs? Notification deadline?). Reference tracking ticket.",

  REDIRECT:
    "Generate a redirect/not-in-scope response. This ticket is not a privacy incident or not PIRT's scope. Explain why briefly, direct to the correct team with specific links/CTI, and close. Common redirects: security vulnerability → SIRT, customer data → Consumer Privacy, access issue → IT Support, Shepherd/AppSec → owning service team.",

  POLICY_ANSWER:
    "Generate a policy answer response. The reporter has a question about privacy policy, not an actual incident. Answer the question directly with a clear yes/no/it depends, cite relevant policy if applicable, provide practical guidance for their specific situation, and close the ticket.",
};

// ─── Input builder ───

interface DraftInput {
  action: string;
  ticket: {
    id: string;
    title: string;
    description: string;
    severity: string;
    jurisdiction: string;
    created: string;
    data_elements: string[];
    containment_status: string;
    days_open: number;
    waiting_on: string;
  };
  conversation_history: Array<{
    author: string;
    date: string;
    summary: string;
  }>;
  notes: string;
}

function buildDraftInput(
  ticket: Ticket,
  responseType: ResponseType,
  additionalContext?: string
): DraftInput {
  // Correspondence will come from the real API in Phase B
  const correspondence: CorrespondenceEntry[] = [];

  const dataElements: string[] = [];
  const desc = (ticket.description || "").toLowerCase();
  if (desc.includes("ssn") || desc.includes("social security")) dataElements.push("SSN");
  if (desc.includes("w2") || desc.includes("w-2")) dataElements.push("W2");
  if (desc.includes("compensation") || desc.includes("salary") || desc.includes("pay")) dataElements.push("Compensation");
  if (desc.includes("bank")) dataElements.push("Bank Account");
  if (desc.includes("passport")) dataElements.push("Passport");
  if (desc.includes("address")) dataElements.push("Home Address");
  if (desc.includes("medical") || desc.includes("health")) dataElements.push("Medical");
  if (desc.includes("diversity")) dataElements.push("Diversity Data");
  if (desc.includes("time-off") || desc.includes("time off")) dataElements.push("Time-Off Records");
  if (dataElements.length === 0) dataElements.push("HR PII (general)");

  let jurisdiction = "USA";
  if (ticket.isEu) {
    if (desc.includes("uk") || desc.includes("united kingdom")) jurisdiction = "UK";
    else if (desc.includes("germany")) jurisdiction = "Germany";
    else if (desc.includes("france")) jurisdiction = "France";
    else jurisdiction = "EU";
  }

  let containment = "unknown";
  if (desc.includes("contained") || desc.includes("revoked") || desc.includes("corrected") || desc.includes("removed")) {
    containment = "confirmed";
  } else if (desc.includes("partial")) {
    containment = "partial";
  } else if (desc.includes("not recoverable") || desc.includes("physical mail")) {
    containment = "not possible (physical)";
  }

  return {
    action: responseType.toLowerCase().replace(/_/g, " "),
    ticket: {
      id: ticket.ticketId,
      title: ticket.title,
      description: ticket.description || "",
      severity: ticket.severity,
      jurisdiction,
      created: ticket.createdAt.split("T")[0],
      data_elements: dataElements,
      containment_status: containment,
      days_open: ticket.daysOpen,
      waiting_on: ticket.waitingOn,
    },
    conversation_history: correspondence.slice(0, 5).map((c) => ({
      author: c.author,
      date: new Date(c.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      summary: c.content.substring(0, 150),
    })),
    notes: additionalContext || "",
  };
}

// ─── Draft generators ───

function generateFirstContact(input: DraftInput): string {
  const { ticket } = input;
  const questions: string[] = [];

  if (ticket.containment_status === "unknown") {
    questions.push("Has the exposure been contained (e.g., access revoked, file deleted, email recalled)?");
  }
  if (ticket.data_elements.includes("HR PII (general)")) {
    questions.push("What specific types of employee data were involved?");
  }
  if (!ticket.description.match(/\d+\s*(employee|record|individual|person)/i)) {
    questions.push("How many employees are affected?");
  }
  if (ticket.jurisdiction === "USA" && !ticket.description.toLowerCase().includes("state")) {
    questions.push("In which state(s) are the affected employees located?");
  }
  if (ticket.containment_status !== "unknown") {
    questions.push("Can you confirm the timeline — when was the exposure discovered and when was it contained?");
  }

  return `Hello,

Thank you for reporting this to the Workplace Trust Privacy Incident Response Team (PIRT). We have reviewed the details.

Assessment: This appears to involve ${ticket.data_elements.join(", ")} — classified as ${ticket.data_elements.some((d) => ["SSN", "Compensation", "Bank Account", "Passport", "Medical", "W2"].includes(d)) ? "Highly Confidential" : "Confidential"} per PXT data classification.${ticket.containment_status === "confirmed" ? " We note positively that containment actions have already been taken." : ""}

To complete our assessment, we need:

${questions.map((q, i) => `${i + 1}. ${q}`).join("\n")}

Please respond at your earliest convenience so we can determine appropriate next steps${ticket.jurisdiction !== "USA" ? " and assess any regulatory notification obligations" : ""}.

Privacy Incident Response Team`;
}

function generateResponse(input: DraftInput): string {
  const { ticket, conversation_history } = input;
  const latestComment = conversation_history.length > 0 ? conversation_history[0] : null;

  return `Hello ${latestComment ? latestComment.author + "@" : ""},

Thank you for ${latestComment ? "the update" : "your response"}. Based on the information provided:

[ACKNOWLEDGE WHAT THEY SAID — e.g., "We understand that access has been revoked as of Jun 15"]

Updated assessment:
- Data: ${ticket.data_elements.join(", ")}
- Containment: ${ticket.containment_status}
- Scope: [UPDATE BASED ON NEW INFO]

Next steps:
1. [WHAT PIRT WILL DO NEXT]
2. [WHAT WE NEED FROM THEM, IF ANYTHING]

${ticket.jurisdiction !== "USA" ? "We are also coordinating with EU Privacy Legal on regulatory assessment.\n\n" : ""}Privacy Incident Response Team`;
}

function generateFollowUpNudge(input: DraftInput): string {
  const { ticket, conversation_history } = input;
  const lastComment = conversation_history.find((c) => c.author === "folaok" || c.author === "tjmalv");
  const daysSince = lastComment ? Math.max(1, ticket.days_open - 3) : ticket.days_open;

  let urgency = "";
  if (ticket.jurisdiction !== "USA") {
    urgency = "\n\nPlease note that regulatory timelines apply given the EU/UK jurisdiction involved.";
  }
  if (ticket.data_elements.some((d) => ["SSN", "W2", "Passport"].includes(d))) {
    urgency = "\n\nGiven the sensitivity of the data involved (${ticket.data_elements.join(", ")}), we are prioritizing resolution.";
  }

  return `Hello ${ticket.waiting_on !== "none" ? ticket.waiting_on + "@" : ""},

Following up on our previous inquiry (~${daysSince} days ago). We still require the following to complete our assessment:

1. [RESTATE OUTSTANDING QUESTION]
2. [RESTATE OUTSTANDING QUESTION]
${urgency}

If we do not receive a response within 5 business days, we will proceed with our assessment based on available information.

Privacy Incident Response Team`;
}

function generateClosure(input: DraftInput): string {
  const { ticket } = input;

  return `Closing. Summary:

- Issue: ${ticket.title}
- Data: ${ticket.data_elements.join(", ")}
- Scope: [NUMBER AFFECTED]
- Containment: ${ticket.containment_status === "confirmed" ? "Verified — exposure remediated" : "[CONFIRM STATUS]"}
- Notification: ${ticket.jurisdiction !== "USA" ? "[EU Legal assessment: required/not required]" : "No state notification obligations identified"}
- Regulatory: ${ticket.jurisdiction !== "USA" ? "[DIM ID / EU Legal disposition]" : "N/A"}

${ticket.containment_status !== "confirmed" ? "Note: [DOCUMENT ANY LIMITATIONS]\n\n" : ""}No further PIRT action required. Please reopen if additional concerns arise.

Privacy Incident Response Team`;
}

function generateLegalEscalation(input: DraftInput): string {
  const { ticket, conversation_history } = input;

  const legalPoc = ticket.jurisdiction === "UK" || ticket.jurisdiction === "EU" || ticket.jurisdiction === "Germany" || ticket.jurisdiction === "France"
    ? "cjohl@amazon.de"
    : "jinouths@";

  const timeline = conversation_history.length > 0
    ? conversation_history.map((c) => `- ${c.date}: ${c.author} — ${c.summary.substring(0, 80)}`).join("\n")
    : "- [TIMELINE NOT AVAILABLE]";

  return `Hi ${legalPoc},

PIRT is referring the following incident for legal assessment on notification obligations.

Summary: ${ticket.title}

Data elements: ${ticket.data_elements.join(", ")}
Classification: ${ticket.data_elements.some((d) => ["SSN", "Compensation", "Bank Account", "Passport", "Medical", "W2"].includes(d)) ? "Highly Confidential" : "Confidential"}
Scope: [NUMBER OF AFFECTED INDIVIDUALS]
Exposure window: [START DATE] to [END DATE]
Containment: ${ticket.containment_status}
Jurisdiction: ${ticket.jurisdiction}

Timeline:
${timeline}

Questions:
1. Does this trigger notification obligations?
2. Which DPAs/state laws apply?
3. Is individual notification required?
4. What is the notification deadline?

Tracking: ${ticket.id}`;
}

function generateRedirect(input: DraftInput): string {
  const { ticket } = input;
  const desc = ticket.description.toLowerCase();

  let team = "[CORRECT TEAM]";
  let reason = "[REASON — e.g., security vulnerability without privacy impact]";
  let link = "[LINK TO CORRECT PROCESS]";

  if (desc.includes("customer") && !desc.includes("employee")) {
    team = "AWS Customer Trust";
    reason = "this involves customer data, not Amazon employee data";
  } else if (desc.includes("shepherd") || desc.includes("appsec") || desc.includes("vulnerability")) {
    team = "Application Security (AppSec)";
    reason = "this is a security vulnerability finding, not a privacy incident involving employee data exposure";
  } else if (desc.includes("harassment") || desc.includes("workplace") || desc.includes("employee relations")) {
    team = "Employee Relations (ER)";
    reason = "this is a workplace conduct matter, not a privacy incident";
    link = "https://er.corp.amazon.com";
  } else if (desc.includes("account") && desc.includes("lock")) {
    team = "IT Support";
    reason = "this is an account access issue, not a privacy incident";
  }

  return `Hello,

Thank you for reporting this. After review, this does not fall within PIRT's scope because ${reason}.

We recommend engaging ${team}${link !== "[LINK TO CORRECT PROCESS]" ? " via " + link : ""}.

If you believe this does involve unauthorized access to or exposure of Amazon employee personal data, please provide additional details and we will reassess.

Privacy Incident Response Team`;
}

function generatePolicyAnswer(_input: DraftInput): string {
  return `Hello,

Thank you for reaching out. This is a policy/process question rather than a privacy incident, so we can address it directly:

[ANSWER THEIR QUESTION — e.g., "Regarding the display of employee names on the wall: names and job titles are classified as General HR PII. Displaying them in a shared workspace with employee consent does not constitute a privacy incident."]

For reference:
- PXT Data Classification Policy: https://policy.a2z.com/docs/567641/publication
- [ADDITIONAL RELEVANT LINK IF APPLICABLE]

If you have further questions about data handling practices, feel free to reach out.

Closing this ticket as no incident response action is required.

Privacy Incident Response Team`;
}

// ─── Main generator ───

const GENERATORS: Record<ResponseType, (input: DraftInput) => string> = {
  FIRST_CONTACT: generateFirstContact,
  RESPONSE: generateResponse,
  FOLLOW_UP_NUDGE: generateFollowUpNudge,
  CLOSURE: generateClosure,
  LEGAL_ESCALATION: generateLegalEscalation,
  REDIRECT: generateRedirect,
  POLICY_ANSWER: generatePolicyAnswer,
};

/**
 * Generate a draft response.
 *
 * Prototype: uses intelligent template generation.
 * Phase B: calls Bedrock/Claude with PIRT_SYSTEM_PROMPT + ACTION_PROMPTS.
 */
export function generateDraft(
  ticket: Ticket,
  responseType: ResponseType,
  additionalContext?: string
): DraftResponse {
  const input = buildDraftInput(ticket, responseType, additionalContext);
  const generator = GENERATORS[responseType];
  const draft = generator(input);

  return {
    draft,
    responseType,
    generatedAt: new Date().toISOString(),
  };
}

/**
 * Get available response types with human-readable labels.
 */
export function getResponseTypeOptions(): Array<{
  value: ResponseType;
  label: string;
  description: string;
}> {
  return [
    { value: "FIRST_CONTACT", label: "First Contact", description: "New ticket — initial triage, assessment, and scoping questions" },
    { value: "RESPONSE", label: "Response", description: "Reply to someone's comment — acknowledge, assess, next steps" },
    { value: "FOLLOW_UP_NUDGE", label: "Follow-Up (Nudge)", description: "No response received — restate questions, add urgency" },
    { value: "CLOSURE", label: "Closure", description: "Close ticket — summarize issue, data, scope, containment, notification" },
    { value: "LEGAL_ESCALATION", label: "Legal Escalation", description: "Referral to Legal POC for notification assessment" },
    { value: "REDIRECT", label: "Redirect (Not Scope)", description: "Not PIRT — explain why, direct to correct team" },
    { value: "POLICY_ANSWER", label: "Policy Answer", description: "Answer a question (not an incident) and close" },
  ];
}

/**
 * Refine an existing draft based on user instruction.
 *
 * Prototype: applies simple text transformations.
 * Phase B: sends draft + instruction to Bedrock as a conversation.
 */
export function refineDraft(
  currentDraft: string,
  instruction: string,
  _ticket: Ticket
): string {
  const lower = instruction.toLowerCase();

  if (lower.includes("shorter") || lower.includes("concise") || lower.includes("brief")) {
    const lines = currentDraft.split("\n").filter((l) => l.trim().length > 0);
    const shortened = lines
      .filter((l) => !l.match(/^(We appreciate|Please note that|This helps us|For reference)/i))
      .join("\n");
    return shortened || currentDraft;
  }

  if (lower.includes("urgent") || lower.includes("urgency") || lower.includes("escalat")) {
    const closingIndex = currentDraft.lastIndexOf("Privacy Incident Response Team");
    if (closingIndex > 0) {
      const before = currentDraft.substring(0, closingIndex).trimEnd();
      return `${before}\n\nThis matter requires immediate attention. Please respond within 24 hours.\n\nPrivacy Incident Response Team`;
    }
  }

  if (lower.includes("deadline") || lower.match(/by\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i)) {
    const dateMatch = instruction.match(/(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+\d{1,2}/i);
    const deadline = dateMatch ? dateMatch[0] : "the end of this week";
    const closingIndex = currentDraft.lastIndexOf("Privacy Incident Response Team");
    if (closingIndex > 0) {
      const before = currentDraft.substring(0, closingIndex).trimEnd();
      return `${before}\n\nPlease respond by ${deadline}. If we do not receive a response by this date, we will proceed based on available information.\n\nPrivacy Incident Response Team`;
    }
  }

  if (lower.includes("remove") && (lower.includes("deadline") || lower.includes("urgency"))) {
    return currentDraft
      .replace(/\n\n.*?respond (within|by).*?\n/gi, "\n")
      .replace(/\n\n.*?immediate attention.*?\n/gi, "\n")
      .replace(/\n\nIf we do not receive.*?\n/gi, "\n");
  }

  if (lower.includes("add") && lower.includes("question")) {
    const topic = instruction.replace(/.*(?:add\s*(?:a\s*)?question\s*(?:about)?)\s*/i, "").trim();
    const closingIndex = currentDraft.lastIndexOf("Privacy Incident Response Team");
    if (closingIndex > 0 && topic) {
      const before = currentDraft.substring(0, closingIndex).trimEnd();
      return `${before}\n\nAdditionally: ${topic.charAt(0).toUpperCase() + topic.slice(1)}?\n\nPrivacy Incident Response Team`;
    }
  }

  if (lower.includes("eu") || lower.includes("gdpr") || lower.includes("dim")) {
    const closingIndex = currentDraft.lastIndexOf("Privacy Incident Response Team");
    if (closingIndex > 0) {
      const before = currentDraft.substring(0, closingIndex).trimEnd();
      return `${before}\n\nAs this may involve EU/UK employee data, we are also assessing GDPR implications. If not already done, please submit a DIM intake: https://gdpr.corp.amazon.com/icm-intake/intake\n\nPrivacy Incident Response Team`;
    }
  }

  // Fallback for prototype
  return currentDraft + `\n\n[Refinement: "${instruction}" — edit manually or use Bedrock AI in Phase B]`;
}

// Export for Phase B Bedrock integration
export { PIRT_SYSTEM_PROMPT as SYSTEM_PROMPT, ACTION_PROMPTS, buildDraftInput };
export type { DraftInput };
